import React, {useCallback, useEffect, useRef, useState} from "react";
import {toast} from "react-toastify";
import SingleChainContainer from "./SingleChainContainer";
import CollapsibleChainGroup from "./CollapsibleChainGroup";
import LoadingAnimation from "../LoadingAnimation";
import EntityView from "../EntityView";
import {Button, Modal} from "react-bootstrap";
import _ from "lodash";
import ResponsiveHelper from "../../lib/ResponsiveHelper";
import NoEvidenceChains from "./NoEvidenceChains";
import {checkOfflineState} from "../OfflineDetect";
import { parseContentDisposition, saveBlobContent } from "../../lib/utils";
import useResizable from "../Resizable";
import useDraggable from "../Draggable";
import Analytics from "../../lib/Analytics";


const DraggableResizableModal = ({
                                   resizable = true,
                                   movable = true,
                                   loading = false,
                                   onHide = () => {},
                                   children,
                                   ...props
                                 }) => {

  // Use an object here so that the drag/move handlers don't need updating
  const [isResizing, setIsResizing] = useState(false);
  const [isMoving, setIsMoving] = useState(false);
  const [translation, setTranslation] = useState({dx: 0, dy: 0});
  const [bounds, setBounds] = useState(null);
  const hasMovedRef = useRef(false);

  const modalRef = useRef();
  const dialogRef = useRef();
  const contentRef = useRef();
  const headerRef = useRef();
  const resizeHandleRef = useRef();

  useEffect(() => {
    if (modalRef.current) {
      dialogRef.current = modalRef.current.dialog;
    }
  }, [modalRef.current])

  useEffect(() => {
    if (dialogRef.current) {
      contentRef.current = dialogRef.current.querySelector(".modal-content");
      headerRef.current = contentRef.current.querySelector(".modal-header");
    }
  }, [dialogRef.current]);

  const handleResize = useCallback(({type}) => {
    const content = contentRef.current;
    if (content) {
      if (!isResizing) {
        setIsResizing(true);
      }
      if (type === "end") {
        setTimeout(() => {
          setIsResizing(false);
        }, 0);
      }
    }
  }, [contentRef.current, isResizing]);
  useResizable(resizeHandleRef, contentRef, {onResize: handleResize});

  const handleMove = useCallback(({type, dx, dy}) => {
    const content = contentRef?.current;
    if (content) {
      if (bounds === null) {
        setBounds(content.getBoundingClientRect());
      }
      const newTransform = `translate(${translation.dx + dx}px, ${translation.dy + dy}px)`;
      if (newTransform !== content.style.transform) {
        content.style.transform = newTransform;
      }
      if (!isMoving) {
        setIsMoving(true);
        hasMovedRef.current = true; // User has manually moved the modal
      }
      if (type === "end") {
        setTimeout(() => {
          setIsMoving(false);
          setTranslation({dx: translation.dx + dx, dy: translation.dy + dy});
        }, 0);
      }
    }
  }, [contentRef.current, isMoving, translation]);
  const constrainPosition = useCallback((dx, dy) => {
    const viewport = ResponsiveHelper.getViewport();
    if (bounds != null) {
      dx = Math.min(Math.max(dx, -(bounds.left + translation.dx)), viewport.width - (bounds.right + translation.dx));
      dy = Math.min(Math.max(dy, -(bounds.top + translation.dy)), viewport.height - (bounds.bottom + translation.dy));
      console.debug(`constrained ${dx} ${dy} translation ${translation.dx} ${translation.dy}`);
    }
    return {dx, dy};
  }, [bounds, translation]);
  useDraggable(headerRef, {onDrag: handleMove, constrain: constrainPosition});

  // Reset transform when loading to allow Bootstrap centering
  useEffect(() => {
    if (loading && contentRef.current && !hasMovedRef.current) {
      contentRef.current.style.transform = '';
      setTranslation({dx: 0, dy: 0});
      setBounds(null);
    }
  }, [loading]);

  const handleHide = useCallback(() => {
    //console.debug(`moving ${isMoving} resizing ${isResizing}`)
    if (!isMoving && !isResizing) {
      onHide();
    }
  }, [isMoving, isResizing]);

  return (
    <Modal ref={modalRef} onHide={handleHide} {...props} >
      {children}
      {resizable && (
        <Modal.Footer style={{ height: '0', overflow: 'visible', position: 'relative', padding: 0, border: 'none' }}>
          <span ref={resizeHandleRef} className="rnd-resizable-handle" style={{ zIndex: 1000 }} />
        </Modal.Footer>
      )}
    </Modal>
  );
};

const EvidenceModal = ({
                         datasets = {},
                         evidenceChains: content = [],
                         exportFn = async () => {},
                         highlightedDatasets = [],
                         isFetching = false,
                         onClose = () => {},
                         showModal,
                         targetID,
                       }) => {

  // Note: do NOT early-return on !showModal here. Hooks below must be
  // called in the same order on every render; bailing out before them
  // changes the hook count between showModal=true and showModal=false
  // and crashes with "Rendered fewer hooks than expected." The hidden
  // case is already handled by <DraggableResizableModal show={showModal}>.
  const defaultSettings = {
    minWidthRatio: 0.8,
    maxWidthRatio: 0.9,
    minHeightRatio: 0.32,
    maxHeightRatio: 0.8
  };

  const respHelper = new ResponsiveHelper(defaultSettings);
  const [isDownloading, setIsDownloading] = useState(false);
  const [isScrolling, setIsScrolling] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [isResizing, setIsResizing] = useState(false);
  const [minWidth, setMinWidth] = useState(respHelper.minWidth);
  const [minHeight, setMinHeight] = useState(respHelper.minHeight);
  const [chainsData, setChainsData] = useState([]);
  const [baseDimensions, setBaseDimensions] = useState();
  const [geometry, setGeometry] = useState({});

  const [scrollEnd, setScrollEnd] = useState(null);
  const [activeComparison, setActiveComparison] = useState(null); // { groupIdx, chainIdx, edgeIdx }
  const [expandedGroupIdx, setExpandedGroupIdx] = useState(null); // Track which group is expanded
  const modalBodyRef = useRef(null);
  const hiddenContentRef = useRef(null);
  const hasMeasured = useRef(false);
  const modalDialogRef = useRef(null);
  const fixedHeaderEntityRef = useRef(null); // Ref for fixed header entity image

  // Observe content changes and shrink modal if there's empty space below
  useEffect(() => {
    if (!modalBodyRef.current || isFetching) return;

    const resizeObserver = new ResizeObserver(() => {
      if (!modalBodyRef.current || !modalDialogRef.current) return;

      const contentHeight = modalBodyRef.current.scrollHeight;
      const viewportHeight = modalBodyRef.current.clientHeight;
      const modalDialog = modalDialogRef.current;

      // If content is shorter than viewport, shrink modal to fit
      if (contentHeight < viewportHeight) {
        const modalHeader = modalDialog.querySelector('.modal-header');
        const modalFooter = modalDialog.querySelector('.modal-footer');
        const headerHeight = modalHeader?.offsetHeight || 0;
        const footerHeight = modalFooter?.offsetHeight || 0;
        const targetHeight = contentHeight + headerHeight + footerHeight + 40; // 40px padding

        modalDialog.style.maxHeight = `${targetHeight}px`;
        modalDialog.style.transition = 'max-height 0.3s ease-out';
      } else {
        // Remove height constraint if content grows again
        modalDialog.style.maxHeight = '';
      }
    });

    resizeObserver.observe(modalBodyRef.current);

    return () => resizeObserver.disconnect();
  }, [isFetching]);

  // Get modal dialog ref when modal renders
  useEffect(() => {
    if (modalBodyRef.current && !modalDialogRef.current) {
      modalDialogRef.current = modalBodyRef.current.closest('.modal-dialog');
    }
  }, [showModal]);

  const handleOpenDialog = () => {
    hasMeasured.current = false;

    // Remove any old chain-X classes (default width will apply)
    const dialogElement = document.querySelector('.ev-ch-mdl');
    if (dialogElement) {
      for (let i = 2; i <= 5; i++) {
        dialogElement.classList.remove(`chain-${i}`);
      }
    }

    const chainsData = deriveChainsData(content);
    setChainsData(chainsData);
  }

  const handleCloseDialog = () => {
    // Remove specific chain-X classes (default width will apply)
    const dialogElement = document.querySelector('.ev-ch-mdl');
    if (dialogElement) {
      for (let i = 2; i <= 5; i++) {
        dialogElement.classList.remove(`chain-${i}`);
      }
    }

    onClose();
    setChainsData([]);
    hasMeasured.current = false;
  };

  const availableBlocks = (totalBlocks, blockWidth) => {
    // TO FIX: remove magic 20 number used for proper block count calculation
    const {width: viewportWidth} = getViewportSize();
    return Math.min(totalBlocks, Math.floor(viewportWidth / (blockWidth + 20)));
  };

  const deriveChainsData = (paths) => {
    return paths
      ? paths.map((path, i) => {
        // Elide any XXX=>patent/pub=>org links to XXX=(patent/pub)>org
        const len = path.length;
        if (len > 3 && path[len - 1].entity.category === 'org'
          && (path[len - 3].entity.category === 'patent'
            || path[len - 3].entity.category === 'publication')) {
          const isPatent = path[len - 3].entity.category === 'patent'
          path.splice(len - 3, 2);
          Object(path[path.length - 2].content).forEach(({dataSet, dataPoints}) => {
            Object(dataPoints).forEach(dp => {
              dp.description = dp.title;
              const docID = dp.source_id.split(':').pop();
              dp.title = `${isPatent ? "Patent" : "PMID"} ${docID}`;
            });
          });
        }
        const respSetting = respHelper.getResponsiveSettings(path.length);
        return {
          idx: i,
          path: path,
          settings: respSetting,
          visibleBlocks: availableBlocks(path.length, respSetting.width)
        };
      })
      : [];
  }

  const getDefaultGeometry = (paths = [], multipleGroups = false) => {
    respHelper.updateInnerParams();

    const {
      width: viewportWidth,
      height: viewportHeight
    } = getViewportSize();

    let maxBlockCount, chainsLengthSet, width, height;
    if (paths.length > 0) {
      maxBlockCount = _(paths)
        .map(x => (x && x.length) || 0)
        .max();
      chainsLengthSet = paths.map(x => (x && x.length) || 0);
      width = respHelper.getTotalWidth(maxBlockCount);
      height = respHelper.getTotalHeight(chainsLengthSet);

      // If there are multiple groups (all collapsed initially), reduce height to fit group headers
      if (multipleGroups) {
        height = Math.min(height * 0.5, viewportHeight * 0.5);
      }
    } else {
      width = respHelper.minWidth;
      height = respHelper.minHeight;
    }

    const x = Math.round(Math.max((viewportWidth - width) / 2, 50));
    const y = Math.round(Math.max((viewportHeight - height) / 4, 5));
    return {
      x, y,
      width: Math.round(Math.min(width, viewportWidth)),
      height: Math.round(Math.min(height, viewportHeight))
    };
  }

  const handleResize = (e, direction, ref, delta, position) => {
    const geometry = getDefaultGeometry(content);
    const {x: x0, y: y0, width: contentWidth, height: contentHeight} = geometry;
    const {x, y} = position || {x: x0, y: y0};
    const width = baseDimensions?.width || contentWidth;
    const height = baseDimensions?.height || contentHeight;
    setGeometry({
                  x, y,
                  width: width + (delta?.width || 0),
                  height: height + (delta?.height || 0)
                });
    const currentChainsData = chainsData;
    const newChainsData = deriveChainsData(currentChainsData.map(x => x.path));

    const shouldUpdateContent = _.zip(currentChainsData, newChainsData).reduce(
      (
        acc,
        [
          {settings: cSet, visibleBlocks: cBC},
          {settings: nSet, visibleBlocks: nBC}
        ]
      ) => {
        return cBC !== nBC || cSet.scale !== nSet.scale || acc;
      },
      false
    );

    if (shouldUpdateContent) {
      setChainsData(newChainsData);
    }
  };

  const handleResizeStart = (e) => {
    //console.log("handleResizeStart");
    setIsResizing(true);
    setBaseDimensions({width: geometry.width, height: geometry.height});
  };

  const handleResizeStop = (e, direction, ref, delta, position) => {
    //console.log("handleResizeStop", position);
    setIsResizing(false);
    setBaseDimensions(null);
    setGeometry({...position, width: ref.style.width, height: ref.style.height});
  };

  const handleScroll = (e) => {
    // because there are no scrollStart/scrollEnd events, use timer delay to simulate scroll end
    //console.log("handleScroll", e);
    if (!isScrolling) {
      setIsScrolling(true);
      if (scrollEnd) {
        clearTimeout(scrollEnd);
      }
      setScrollEnd(setTimeout(() => {
        setScrollEnd(null);
        setIsScrolling(false);
      }, 2000));
    }
  };

  const updateContent = (scaleAction) => {
    //console.log("updateContent");
    setChainsData(chainsData.map(chainToUpdate => {
      const newSetting = respHelper[scaleAction](chainToUpdate.settings);
      return {
        ...chainToUpdate,
        visibleBlocks: availableBlocks(chainToUpdate.path.length, newSetting.width),
        settings: newSetting
      };
    }));
  };

  // Group chains by their LHS (start) entity ID
  const groupChainsByLHS = (chains) => {
    const groups = {};

    chains.forEach(chainData => {
      const { path } = chainData;
      if (path.length === 0) return;

      const startEntity = path[0].entity;
      const endEntity = path[path.length - 1].entity;
      const startId = startEntity?.id || 'unknown';

      if (!groups[startId]) {
        groups[startId] = {
          startEntity,
          endEntities: [],
          paths: []
        };
      }

      groups[startId].paths.push(chainData);

      // Track unique end entities
      const endId = endEntity?.id;
      if (endId && !groups[startId].endEntities.some(e => e.id === endId)) {
        groups[startId].endEntities.push(endEntity);
      }
    });

    return Object.values(groups);
  };

  const handleZoomIn = () => {
    updateContent("increaseScale");
  };

  const handleZoomOut = () => {
    updateContent("decreaseScale");
  };

  const canInc = () => {
    return chainsData.length > 0 && chainsData.some(el => el.settings.canInc);
  };

  const canDec = () => {
    return chainsData.length > 0 && chainsData.some(el => el.settings.canDec);
  };

  useEffect(() => {
    setChainsData(deriveChainsData(content));
  }, [content]);

  useEffect(() => {
    showModal ? handleOpenDialog() : handleCloseDialog();
  }, [showModal]);

  // Add chain length class to dialog for width sizing
  useEffect(() => {
    if (!showModal || chainsData.length === 0 || isFetching) return;
    if (hasMeasured.current) return;

    hasMeasured.current = true;

    // Get maximum chain length (count nodes only, not edges)
    // Path alternates: node, edge, node, edge, node... so filter by .entity property
    const maxChainLength = Math.max(...chainsData.map(chain =>
      chain.path.filter(item => item.entity).length
    ));
    const chainClass = `chain-${maxChainLength}`;

    // Wait for modal to be fully rendered and visible before adding chain class
    // This ensures Bootstrap's modal animation doesn't interfere with our transition
    setTimeout(() => {
      const dialogElement = document.querySelector('.ev-ch-mdl');

      if (!dialogElement) {
        hasMeasured.current = false;
        return;
      }

      // Add specific chain class to trigger width transition
      dialogElement.classList.add(chainClass);
    }, 50);
  }, [chainsData, showModal, isFetching]);

  const getViewportSize = () => {
    return ResponsiveHelper.getViewport();
  };

  useEffect(() => {
    window.addEventListener("resize", handleWindowResize);
    return () => window.removeEventListener("resize", handleWindowResize);
  });

  const handleWindowResize = () => {
    if (showModal) {
      setGeometry(getDefaultGeometry(content));
      //console.log("handleWindowResize geometry", geometry);
      const modalDOMElements = document.getElementsByClassName(
        "ev-ch-dialog"
      );
      if (modalDOMElements.length > 0) {
        handleResize(null, null, modalDOMElements[0]);
      }
    }
  };

  // Handle comparison toggle - only one comparison open across entire modal
  const handleComparisonToggle = (groupIdx, chainIdx, edgeIdx) => {
    console.log('[EvidenceModal] handleComparisonToggle called:', { groupIdx, chainIdx, edgeIdx });
    console.log('[EvidenceModal] Current activeComparison:', activeComparison);
    const isCurrentlyOpen = activeComparison?.groupIdx === groupIdx &&
                            activeComparison?.chainIdx === chainIdx &&
                            activeComparison?.edgeIdx === edgeIdx;
    const newComparison = isCurrentlyOpen ? null : { groupIdx, chainIdx, edgeIdx };
    console.log('[EvidenceModal] Setting activeComparison to:', newComparison);
    if (!isCurrentlyOpen) {
      Analytics.event(Analytics.EVENTS.COMPOUND_SIMILARITY, { target_id: targetID, group: groupIdx, chain: chainIdx, edge: edgeIdx });
    }
    setActiveComparison(newComparison);
  };


  // Handle group expansion - only one group expanded at a time
  const handleGroupToggle = (groupIdx) => {
    setExpandedGroupIdx(prevIdx => {
      const isExpanding = prevIdx !== groupIdx;
      if (isExpanding) {
        Analytics.event(Analytics.EVENTS.ECHAIN_EXPAND, { target_id: targetID, group: groupIdx });
      }
      return isExpanding ? groupIdx : null;
    });
  };

  const handleExportClick = e => {
    let filename = null;
    if (!window.navigator.onLine) {
      e.preventDefault();
    } else {
      Analytics.event(Analytics.EVENTS.ECHAIN_EXPORT, { target_id: targetID });
      setIsDownloading(true);
      // TODO: ensure provided function is async
      exportFn(targetID)
        .then(rsp => {
          const header = rsp.headers.get('content-disposition');
          const disposition = parseContentDisposition(header);
          filename = disposition.parameters.filename;
          return rsp;
        }).then(rsp => rsp.blob())
        .then(blob => {
          saveBlobContent(blob, filename);
        })
        .catch(err => {
          toast.error(`Evidence export failed (${err})`);
        })
        .finally(() => {
          setIsDownloading(false);
        });
    }
  };

  return (
    <DraggableResizableModal
      show={showModal}
      onHide={handleCloseDialog}
      loading={isFetching}
      dialogClassName="ev-ch-mdl"
      scrollable={!isFetching}
      resizable={!isFetching}
    >
      <Modal.Header
        closeButton
      >
        <Modal.Title>
          Supporting Data
          {`${isFetching ? "" : " (" + content.length + " paths)"}`}
        </Modal.Title>
        {!isFetching && content.length > 0 && (
          <div className="ev-ch-controls">
            <Button
              className="ev-ch-zoom-btn"
              onClick={handleZoomIn}
              disabled={!canInc()}
              title="Zoom in"
            >
              <span className="glyphicon glyphicon-zoom-in"/>
            </Button>
            <Button
              className="ev-ch-zoom-btn"
              onClick={handleZoomOut}
              disabled={!canDec()}
              title="Zoom out"
            >
              <span className="glyphicon glyphicon-zoom-out"/>
            </Button>
            <Button
              className={`ev-ch-zoom-btn ev-ch-export-btn${isDownloading ? ' export-icon-pulse' : ''}`}
              onClick={checkOfflineState(handleExportClick)}
              disabled={isDownloading}
            >
              <span className="glyphicon glyphicon-download-alt"/>
            </Button>
          </div>
        )}
      </Modal.Header>
      <Modal.Body
        ref={modalBodyRef}
        className="ev-ch-mdl-body"
        onScroll={handleScroll}
        style={isFetching ? {
          maxWidth: '450px',
          margin: '0 auto',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '200px'
        } : {}}
      >
        {/* Animation overlay container for portaled content */}
        <div id="ev-ch-animation-overlay" style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          pointerEvents: 'none',
          zIndex: 9999
        }} />
        {isFetching ? (
          <LoadingAnimation/>
        ) : !content.length ? (
          <NoEvidenceChains/>
        ) : (
          <div ref={hiddenContentRef} style={{ width: '100%' }}>
            {(() => {
              const groups = groupChainsByLHS(chainsData);
              // Determine common end node (assuming all chains share the same end node)
              const commonEndEntity = groups.length > 0 && groups[0].endEntities.length > 0
                ? groups[0].endEntities[0]
                : null;

              return (
                <div className="ev-ch-content">
                  {commonEndEntity && (
                    <div className="ev-ch-fixed-header">
                      <div className="ev-ch-header-content-wrapper">
                        <span className="ev-ch-header-label">Evidence leading to</span>
                        <span className="ev-ch-header-entity-label">{commonEndEntity.label || commonEndEntity.name}</span>
                      </div>
                      <div className="ev-ch-header-entity-wrapper">
                        <EntityView showLabel={true}
                          entity={commonEndEntity}
                          dataset={datasets[commonEndEntity?.dataset]}
                          scale="collapsed"
                          showCategory={false}
                          truncate={false}
                          entityImageRef={fixedHeaderEntityRef}
                        />
                      </div>
                    </div>
                  )}
                  {groups.map((group, groupIdx) => (
                    <CollapsibleChainGroup
                      key={groupIdx}
                      paths={group.paths}
                      startEntity={group.startEntity}
                      endEntities={group.endEntities}
                      isScrolling={isScrolling}
                      datasets={datasets}
                      highlightedDatasets={highlightedDatasets}
                      baseIdx={groupIdx}
                      hideEndNodes={true}
                      isExpanded={groups.length === 1 || expandedGroupIdx === groupIdx}
                      isSingleGroup={groups.length === 1}
                      onToggle={() => handleGroupToggle(groupIdx)}
                      activeComparison={activeComparison}
                      onComparisonToggle={handleComparisonToggle}
                      fixedHeaderEntityRef={fixedHeaderEntityRef}
                      scrollContainerRef={modalBodyRef}
                    />
                  ))}
                </div>
              );
            })()}
          </div>
        )}
      </Modal.Body>
    </DraggableResizableModal>
  );
};

export default EvidenceModal;
